import { Link } from '@farmersdog/corgi-legacy';
import { useViewport, Text, Grid, Divider } from '@farmersdog/corgi-x';

import { useGetLead } from '../../../graphql/queries';

import { Benefits } from './Benefits';
import { Callout } from './Callout';
import styles from './GetStartedVetCustomizedPlan.module.css';
import { Hero } from './Hero';

import type { VetCustomPlanContextType } from '../../../hooks/useVetCustomizedPlan';

interface GetStartedVetCustomizedPlanProps {
  handleNavigateNext: () => void;
  nextSignupRoute?: string;
  customizedPlan: VetCustomPlanContextType;
}

export function GetStartedVetCustomizedPlan({
  handleNavigateNext,
  nextSignupRoute,
  customizedPlan,
}: GetStartedVetCustomizedPlanProps) {
  const lead = useGetLead().data?.lead ?? null;

  const { md, xs } = useViewport();
  const isMobile = xs && !md;
  const dogName = customizedPlan?.dogName ?? 'your dog';
  const vetName = customizedPlan?.vetName ?? 'your vet';
  const firstName = lead?.firstName ?? '';
  const freshRecipeNames = customizedPlan?.freshRecipeNames;
  const diyRecipeDisplayNames = customizedPlan?.diyRecipeDisplayNames;

  const pet = lead?.pets.find(({ name }) => name === dogName);

  const hasAdditionalInformation =
    pet?.activityLevel ||
    pet?.bodyCondition ||
    pet?.weight ||
    pet?.targetWeight;

  return (
    <section
      className={styles.container}
      aria-label="Get Started Vet Customized Plan"
    >
      <Hero
        firstName={firstName}
        vetName={vetName}
        dogName={dogName}
        isMobile={isMobile}
        freshRecipeNames={freshRecipeNames}
        diyRecipeDisplayNames={diyRecipeDisplayNames}
      />
      <div className={styles.body}>
        <div className={styles.benefits}>
          <Benefits isMobile={isMobile} dogName={dogName} />
        </div>
        <Divider color="Kale1" width={1} spacing={48} />
        <div>
          <Text
            variant={isMobile ? 'heading-22' : 'heading-28'}
            as="h2"
            topSpacing={isMobile ? 'none' : 'xxl'}
            bold
          >
            Let’s determine {dogName}’s portions and daily price!
          </Text>
          <Text
            variant={isMobile ? 'article-16' : 'article-20'}
            topSpacing="sm"
            className={styles.info}
          >
            We use information like age, breed, and weight to calculate exactly
            how much {dogName} should eat per day.
          </Text>
          {hasAdditionalInformation && (
            <Grid justify="center" topSpacing="md">
              <Callout
                vetName={vetName}
                dogName={dogName}
                isMobile={isMobile}
              />
            </Grid>
          )}
          <Text
            topSpacing="md"
            variant={isMobile ? 'article-12' : 'article-16'}
            italic
            className={styles.note}
          >
            {hasAdditionalInformation ? 'The rest' : 'This'} will only take ~3
            minutes!
          </Text>
          {isMobile && <Divider color="Kale1" width={1} spacing={24} />}
          <div className={styles.buttonContainer}>
            <Link
              asButton
              asPill
              to={nextSignupRoute}
              onClick={handleNavigateNext}
              aria-label="Continue to signup"
              className={styles.cta}
            >
              Complete Sign-up
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
